// ─────────────────────────────────────────────────────────────────────────────
// useLoginForm — owns login form state + submit orchestration, so the page is
// presentational. Consumes only the auth ACTIONS context (no re-render on
// user/loading changes elsewhere). Handles the mfa_required branch that the old
// Login page silently dropped (bug B4), plus the email-MFA affordances:
// resendCode (re-email a fresh code), cancelMfa (back to sign in), and an
// `info` message for "code sent" feedback. The 6-digit code itself is entered
// via the segmented <OtpInput/>, which owns digit sanitising.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { useAuthActions } from "../model/AuthProvider";

// Fallback code lifetime if the backend doesn't send `expires_in` (it does — see
// /auth/login + /auth/mfa/resend). MAX_RESENDS mirrors the backend cap.
const DEFAULT_TTL_SEC = 60;
const MAX_RESENDS      = 3;

export function useLoginForm() {
  const { login, verifyMfa, resendMfa, clearMfa } = useAuthActions();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  // MFA code countdown: `codeEpoch` bumps every time a code is (re)sent, which
  // restarts the timer; `secondsLeft` ticks down to 0 (= expired). `codeTtl` is
  // whatever the backend reported via `expires_in` (kept in sync, no drift).
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [codeTtl, setCodeTtl] = useState(DEFAULT_TTL_SEC);
  const [codeEpoch, setCodeEpoch] = useState(0);
  const [resendCount, setResendCount] = useState(0);

  const setField = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  const togglePassword = () => setShowPassword((s) => !s);

  // Restart the countdown whenever a new code is sent (codeEpoch changes).
  useEffect(() => {
    if (codeEpoch === 0) return undefined; // no code sent yet
    setSecondsLeft(codeTtl);
    const id = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) { clearInterval(id); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [codeEpoch, codeTtl]);

  const codeExpired  = codeEpoch > 0 && secondsLeft === 0;
  const resendsLeft  = Math.max(0, MAX_RESENDS - resendCount);
  // Resend only once the current code has expired (natural cooldown) and while
  // the per-challenge cap has not been hit.
  const canResend    = codeExpired && resendsLeft > 0 && !loading;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.email || !form.password) {
      setError("Please fill in all fields.");
      return;
    }
    setError("");
    setInfo("");
    setLoading(true);
    const result = await login({ email: form.email, password: form.password });
    setLoading(false);
    if (result?.mfa_required) {
      setMfaRequired(true); // B4: surface the MFA step instead of doing nothing
      setResendCount(0);
      setCodeTtl(result.expires_in || DEFAULT_TTL_SEC);
      setCodeEpoch((e) => e + 1); // start the countdown
      setInfo(`We emailed a 6-digit code to ${form.email}.`);
      return;
    }
    if (!result?.success) setError(result?.message || "Invalid email or password.");
  };

  const submitMfa = async (e) => {
    e?.preventDefault?.();
    if (!mfaCode) {
      setError("Enter your verification code.");
      return;
    }
    setError("");
    setLoading(true);
    const result = await verifyMfa(mfaCode);
    setLoading(false);
    if (!result?.success) setError(result?.message || "Invalid verification code.");
  };

  // Re-send the code for the current MFA challenge. Guarded by `canResend`
  // (cooldown + cap); the backend enforces the same MAX_RESENDS limit.
  const resendCode = async () => {
    if (!canResend) return;
    setError("");
    setInfo("");
    setLoading(true);
    const result = await resendMfa();
    setLoading(false);
    if (result?.success) {
      setMfaCode("");
      setResendCount((c) => c + 1);
      setCodeTtl(result.expires_in || DEFAULT_TTL_SEC);
      setCodeEpoch((e) => e + 1); // restart the countdown
      setInfo(`A new code was sent to ${form.email}.`);
    } else {
      setError(result?.message || "Couldn't resend the code. Please sign in again.");
    }
  };

  // Abandon the MFA step and return to the sign-in form.
  const cancelMfa = () => {
    clearMfa();
    setMfaRequired(false);
    setMfaCode("");
    setError("");
    setInfo("");
    setSecondsLeft(0);
    setCodeEpoch(0);
    setResendCount(0);
  };

  return {
    form, setField, showPassword, togglePassword,
    loading, error, info,
    mfaRequired, mfaCode, setMfaCode,
    secondsLeft, codeExpired, canResend, resendsLeft,
    submit, submitMfa, resendCode, cancelMfa,
  };
}

export default useLoginForm;
